import React from "react";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SettingsIcon from "@mui/icons-material/Settings";
import BusinessIcon from "@mui/icons-material/Business";
import RuleFolderOutlinedIcon from "@mui/icons-material/RuleFolderOutlined";

import useSmartParse from "../../../hooks/super/useSmartParse";
import useOrganisation from "../../../hooks/super/useOrganisation";
import useAuth from "../../../hooks/useAuth";

import { CollapseAlert, SnackbarAlert } from "../../../components/pages/common/styled";
import { UserType, SmartParseSensitivity, WafAction } from "../../../utils/constants";
import PageHeader from "../../../components/pages/_new/super/common/PageHeader";
import PrimaryButton from "../../../components/pages/_new/super/common/PrimaryButton";
import OutlinedButton from "../../../components/pages/_new/super/common/OutlinedButton";
import {
  TOKENS,
  StatTile,
  StatusPill,
  Monogram,
  EmptyState,
} from "../../../components/pages/_new/super/design";
import BulkUpdateModal from "./BulkUpdateModal";
import OrgUpdateModal from "./OrgUpdateModal";

const thCellSx = {
  padding: "10px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.bgSunken,
  color: TOKENS.fg2,
  fontSize: 11.5,
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
};
const tdCellSx = {
  padding: "12px 14px",
  borderBottom: `1px solid ${TOKENS.borderSubtle}`,
  color: TOKENS.fg1,
  fontSize: 13.5,
};

function SASmartParse() {
  const {
    sites,
    sitesTotal,
    overview,
    loading,
    errMsg,
    getSmartParseSites,
    getSmartParseOverview,
    bulkUpdateSmartParse,
    updateSmartParseByOrganisation,
    setErr,
  } = useSmartParse();
  const { organisations, getOrganisations } = useOrganisation();
  const { isAuthenticated, adminUserType } = useAuth();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(20);
  const [searchValue, setSearchValue] = React.useState("");
  const [enabledFilter, setEnabledFilter] = React.useState("");
  const [actionFilter, setActionFilter] = React.useState("");
  const [selectedSites, setSelectedSites] = React.useState([]);
  const [bulkModalOpen, setBulkModalOpen] = React.useState(false);
  const [orgModalOpen, setOrgModalOpen] = React.useState(false);

  const [snackOpen, setSnackOpen] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [success, setSuccess] = React.useState("success");

  const handleSnackClose = () => setSnackOpen(false);

  const [errOpen, setErrOpen] = React.useState(false);
  React.useEffect(() => {
    if (errMsg) setErrOpen(true);
  }, [errMsg]);

  React.useEffect(() => {
    if (isAuthenticated) {
      getSmartParseSites(rowsPerPage, page + 1, {
        enabled: enabledFilter !== "" ? enabledFilter : undefined,
        action: actionFilter !== "" ? actionFilter : undefined,
        search: searchValue || undefined,
      });
      getSmartParseOverview();
      getOrganisations();
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    getSmartParseSites(rowsPerPage, page + 1, {
      enabled: enabledFilter !== "" ? enabledFilter : undefined,
      action: actionFilter !== "" ? actionFilter : undefined,
      search: searchValue || undefined,
    });
    getSmartParseOverview();
    setSelectedSites([]);
  };

  const handleSearch = () => {
    setPage(0);
    getSmartParseSites(rowsPerPage, 1, {
      enabled: enabledFilter !== "" ? enabledFilter : undefined,
      action: actionFilter !== "" ? actionFilter : undefined,
      search: searchValue || undefined,
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    getSmartParseSites(rowsPerPage, newPage + 1, {
      enabled: enabledFilter !== "" ? enabledFilter : undefined,
      action: actionFilter !== "" ? actionFilter : undefined,
      search: searchValue || undefined,
    }, false);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    getSmartParseSites(newRowsPerPage, 1, {
      enabled: enabledFilter !== "" ? enabledFilter : undefined,
      action: actionFilter !== "" ? actionFilter : undefined,
      search: searchValue || undefined,
    });
  };

  const handleSelectAll = (event) => {
    if (event.target.checked && sites) setSelectedSites(sites.map((site) => site.site_uid));
    else setSelectedSites([]);
  };
  const handleSelectSite = (siteUid) => {
    setSelectedSites((prev) => {
      if (prev.includes(siteUid)) return prev.filter((id) => id !== siteUid);
      return [...prev, siteUid];
    });
  };

  const handleBulkUpdate = async (config) => {
    const result = await bulkUpdateSmartParse(selectedSites, config);
    if (result.success) {
      setMessage(`Successfully updated ${result.updated} sites`);
      setSuccess("success");
      setSelectedSites([]);
    } else {
      setMessage(result.error || "Failed to update sites");
      setSuccess("error");
    }
    setSnackOpen(true);
    setBulkModalOpen(false);
  };

  const handleOrgUpdate = async (orgId, config) => {
    const result = await updateSmartParseByOrganisation(orgId, config);
    if (result.success) {
      setMessage(`Successfully updated ${result.sites_updated} sites for ${result.organisation}`);
      setSuccess("success");
    } else {
      setMessage(result.error || "Failed to update organisation sites");
      setSuccess("error");
    }
    setSnackOpen(true);
    setOrgModalOpen(false);
  };

  const getSensitivityLabel = (value) => {
    switch (value) {
      case SmartParseSensitivity.LOW:
        return "Low";
      case SmartParseSensitivity.MEDIUM:
        return "Medium";
      case SmartParseSensitivity.HIGH:
        return "High";
      default:
        return "Unknown";
    }
  };

  const getActionLabel = (value) => {
    switch (value) {
      case WafAction.DETECT:
        return "Detect";
      case WafAction.BLOCK:
        return "Block";
      case WafAction.CHALLENGE:
        return "Challenge";
      default:
        return "Unknown";
    }
  };

  const getActionStatus = (value) => {
    switch (value) {
      case WafAction.DETECT:
        return "Trial";
      case WafAction.BLOCK:
        return "Expired";
      case WafAction.CHALLENGE:
        return "Suspended";
      default:
        return "Pending";
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: `${TOKENS.radiusXs}px`,
      background: TOKENS.bgSurface,
    },
  };
  const labelSx = { fontSize: 12.5, color: TOKENS.fg2, mb: 0.75, fontWeight: 500 };
  const isSuper = UserType.SUPER_ADMIN === adminUserType;

  return (
    <React.Fragment>
      <Helmet title="SA SmartParse WAF" />
      <PageHeader
        title="SmartParse Management"
        description="Per-site SmartParse / ML-WAF configuration with bulk and per-organisation operations"
        actions={
          <Stack direction="row" spacing={1.5}>
            <OutlinedButton onClick={handleRefresh} startIcon={<RefreshIcon style={{ width: 18, height: 18 }} />}>
              Refresh
            </OutlinedButton>
            {isSuper && (
              <OutlinedButton
                onClick={() => setOrgModalOpen(true)}
                startIcon={<BusinessIcon style={{ width: 18, height: 18 }} />}
              >
                Update by Org
              </OutlinedButton>
            )}
            {selectedSites.length > 0 && isSuper && (
              <PrimaryButton
                onClick={() => setBulkModalOpen(true)}
                startIcon={<SettingsIcon style={{ width: 18, height: 18 }} />}
              >
                Bulk Update ({selectedSites.length})
              </PrimaryButton>
            )}
          </Stack>
        }
      />
      <Box sx={{ mb: 3 }}>
        <CollapseAlert errOpen={errOpen} setErrOpen={setErrOpen} setErr={setErr} errMsg={errMsg} />
      </Box>

      {overview && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
            gap: 2,
            mb: 3,
          }}
        >
          <StatTile icon={<RuleFolderOutlinedIcon />} value={overview.total_sites || 0} label="Total sites" />
          <StatTile
            icon={<RuleFolderOutlinedIcon />}
            iconBg={TOKENS.successBg}
            iconInk={TOKENS.successInk}
            value={overview.smart_parse?.enabled || 0}
            label="SmartParse enabled"
          />
          <StatTile
            icon={<RuleFolderOutlinedIcon />}
            iconBg={TOKENS.bgSunken}
            iconInk={TOKENS.fg3}
            value={overview.smart_parse?.disabled || 0}
            label="Disabled"
          />
          <StatTile
            icon={<RuleFolderOutlinedIcon />}
            iconBg={TOKENS.dangerBg}
            iconInk={TOKENS.dangerInk}
            value={overview.smart_parse?.block_mode || 0}
            label="Block mode"
          />
        </Box>
      )}

      <Box
        sx={{
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusM}px`,
          background: TOKENS.bgSurface,
          boxShadow: TOKENS.shadowXs,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: "14px 16px",
            borderBottom: `1px solid ${TOKENS.borderSubtle}`,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            placeholder="Search by site ID..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: TOKENS.fg3 }} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 260, ...inputSx }}
          />
          <Box sx={{ minWidth: 140 }}>
            <FormControl fullWidth size="small">
              <Select
                value={enabledFilter}
                onChange={(e) => {
                  setEnabledFilter(e.target.value);
                  setPage(0);
                  getSmartParseSites(rowsPerPage, 1, {
                    enabled: e.target.value !== "" ? e.target.value : undefined,
                    action: actionFilter !== "" ? actionFilter : undefined,
                    search: searchValue || undefined,
                  });
                }}
                displayEmpty
                sx={{ borderRadius: `${TOKENS.radiusXs}px`, "& .MuiOutlinedInput-input": { fontSize: 14 } }}
              >
                <MenuItem value="">All status</MenuItem>
                <MenuItem value={true}>Enabled</MenuItem>
                <MenuItem value={false}>Disabled</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ minWidth: 140 }}>
            <FormControl fullWidth size="small">
              <Select
                value={actionFilter}
                onChange={(e) => {
                  setActionFilter(e.target.value);
                  setPage(0);
                  getSmartParseSites(rowsPerPage, 1, {
                    enabled: enabledFilter !== "" ? enabledFilter : undefined,
                    action: e.target.value !== "" ? e.target.value : undefined,
                    search: searchValue || undefined,
                  });
                }}
                displayEmpty
                sx={{ borderRadius: `${TOKENS.radiusXs}px`, "& .MuiOutlinedInput-input": { fontSize: 14 } }}
              >
                <MenuItem value="">All actions</MenuItem>
                <MenuItem value={0}>Detect</MenuItem>
                <MenuItem value={1}>Block</MenuItem>
                <MenuItem value={2}>Challenge</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <OutlinedButton onClick={handleSearch}>Search</OutlinedButton>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: labelSx.fontSize, color: TOKENS.fg3 }}>
            {loading ? "Loading..." : `${sites?.length || 0} of ${sitesTotal || 0}`}
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={thCellSx}>
                  <Checkbox
                    size="small"
                    indeterminate={selectedSites.length > 0 && sites && selectedSites.length < sites.length}
                    checked={sites && sites.length > 0 && selectedSites.length === sites.length}
                    onChange={handleSelectAll}
                    disabled={!isSuper}
                  />
                </TableCell>
                <TableCell sx={thCellSx}>Site</TableCell>
                <TableCell sx={thCellSx}>Organisation</TableCell>
                <TableCell sx={thCellSx}>Status</TableCell>
                <TableCell sx={thCellSx}>Sensitivity</TableCell>
                <TableCell sx={thCellSx}>Action</TableCell>
                <TableCell sx={thCellSx}>Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6, border: "none" }}>
                    <CircularProgress size={26} sx={{ color: TOKENS.primary }} />
                  </TableCell>
                </TableRow>
              ) : sites && sites.length > 0 ? (
                sites.map((site) => (
                  <TableRow
                    key={site.site_uid}
                    hover
                    selected={selectedSites.includes(site.site_uid)}
                    sx={{ "&:hover": { background: TOKENS.bgSunken } }}
                  >
                    <TableCell padding="checkbox" sx={tdCellSx}>
                      <Checkbox
                        size="small"
                        checked={selectedSites.includes(site.site_uid)}
                        onChange={() => handleSelectSite(site.site_uid)}
                        disabled={!isSuper}
                      />
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      <Stack direction="row" alignItems="center" spacing={1.25}>
                        <Monogram name={site.site_id || "?"} size={32} />
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: TOKENS.fg1 }}>
                          {site.site_id}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ ...tdCellSx, color: TOKENS.fg2 }}>{site.owner?.title || "—"}</TableCell>
                    <TableCell sx={tdCellSx}>
                      <StatusPill status={site.smart_parse?.enabled ? "Active" : "Pending"} />
                    </TableCell>
                    <TableCell sx={{ ...tdCellSx, color: TOKENS.fg2 }}>
                      {site.smart_parse?.enabled ? getSensitivityLabel(site.smart_parse?.sensitivity) : "—"}
                    </TableCell>
                    <TableCell sx={tdCellSx}>
                      {site.smart_parse?.enabled ? (
                        <StatusPill status={getActionStatus(site.smart_parse?.action)} />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={{ ...tdCellSx, color: TOKENS.fg2 }}>
                      {site.updated_at ? new Date(site.updated_at).toLocaleDateString() : "—"}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} sx={{ border: "none" }}>
                    <EmptyState
                      icon={<RuleFolderOutlinedIcon />}
                      title="No sites found"
                      body="Try a different keyword or filter."
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={sitesTotal}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </Box>

      <BulkUpdateModal open={bulkModalOpen} onClose={() => setBulkModalOpen(false)} onSubmit={handleBulkUpdate} selectedCount={selectedSites.length} />
      <OrgUpdateModal
        open={orgModalOpen}
        onClose={() => setOrgModalOpen(false)}
        onSubmit={handleOrgUpdate}
        organisations={organisations || []}
        loading={loading}
      />
      <SnackbarAlert open={snackOpen} onClose={handleSnackClose} severity={success} message={message} />
    </React.Fragment>
  );
}

export default SASmartParse;
